<script setup lang="ts">
/* eslint-env vue/setup-compiler-macros */
import {
    computed,
    nextTick,
    ref,
} from 'vue';
import DatePicker from 'primevue/datepicker';
import Popover from 'primevue/popover';
import Select from 'primevue/select';
import {
    cloneDateRangeValue,
    dateRangePresets,
    getMatchingPreset,
    getPresetRange,
    toCommittedRange,
    toDraftRange,
    type DateRangeDraft,
    type DateRangePresetId,
    type DateRangeValue,
} from '@/utils/datePickerPresets';
import {
    calendarDt as sharedCalendarDt,
    calendarPt as sharedCalendarPt,
} from '@/utils/datePickerTheme';
import { calendarToJSDate, todayIn } from '@/utils/dt';
import { useTimezoneStore } from '@/stores/timezone';

interface Props {
    modelValue: DateRangeValue
    label?: string
    triggerWidth?: string
    triggerRadius?: string
}

interface CalendarHeaderItem {
    key: string
    index: number
    month: number
    year: number
    monthLabel: string
}

interface DatePickerInstance {
    currentMonth: number | null
    currentYear: number | null
    currentView: string
    onMonthDropdownChange?: (value: string) => void
    onYearDropdownChange?: (value: string) => void
}

const props = withDefaults(defineProps<Props>(), {
    label: 'Date:',
    triggerWidth: '265px',
    triggerRadius: '20px',
});

const emit = defineEmits(['update:modelValue']);

const VISIBLE_MONTH_COUNT = 2;
const YEAR_OPTIONS_BUFFER = 10;

const tzStore = useTimezoneStore();

const popoverRef = ref();
const datePickerRef = ref<DatePickerInstance | null>(null);
const draftRange = ref<DateRangeDraft>(toDraftRange(props.modelValue));

const dateFormatter = new Intl.DateTimeFormat('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
});

const monthFormatter = new Intl.DateTimeFormat('en-US', {
    month: 'long',
});

const calendarDt = {
    ...sharedCalendarDt,
    panel: {
        padding: '0',
        background: 'transparent',
        borderColor: 'transparent',
        borderRadius: '0',
        color: '#344054',
        shadow: 'none',
    },
    header: {
        padding: '0 0 0.5rem',
        background: 'transparent',
        color: '#344054',
        borderColor: 'transparent',
    },
    group: {
        borderColor: 'transparent',
    },
};

const calendarPt = {
    ...sharedCalendarPt,
    root: {
        class: 'date-range-picker__calendar',
    },
    panel: {
        style: {
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            height: '100%',
            minWidth: '0',
            minHeight: '0',
            overflow: 'hidden',
            boxShadow: 'none',
        },
    },
    calendarContainer: {
        class: 'date-range-picker__calendar-container',
    },
    header: {
        class: 'date-range-picker__calendar-header',
        style: {
            display: 'none',
        },
    },
    title: {
        class: 'date-range-picker__calendar-title',
    },
    selectMonth: {
        disabled: false,
        style: {
            fontSize: '11px',
            cursor: 'pointer',
        },
    },
    selectYear: {
        disabled: false,
        style: {
            fontSize: '15px',
            cursor: 'pointer',
        },
    },
};

const yearSelectPt = {
    root: {
        class: 'date-range-picker__year-select',
        style: {
            display: 'inline-flex',
            alignItems: 'center',
            width: 'auto',
            minWidth: '0',
            minHeight: 'auto',
            padding: '0',
            border: 'none',
            background: 'transparent',
            boxShadow: 'none',
            gap: '0',
        },
    },
    label: {
        class: 'date-range-picker__year-select-label',
        style: {
            padding: '0',
            marginLeft: '3px',
            minHeight: 'auto',
            fontFamily: 'Archivo, sans-serif',
            fontSize: '15px',
            fontWeight: '500',
            lineHeight: '1',
            color: '#000000',
        },
    },
    dropdown: {
        class: 'date-range-picker__year-select-dropdown',
        style: {
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '10px',
            padding: '0',
            marginLeft: '3px',
            border: 'none',
            background: 'transparent',
            color: 'rgba(0, 0, 0, 0.55)',
        },
    },
    dropdownIcon: {
        class: 'date-range-picker__year-select-dropdown-icon',
        style: {
            fontSize: '6px',
        },
    },
    overlay: {
        class: 'date-range-picker__year-select-overlay',
        style: {
            marginTop: '4px',
            marginLeft: '-6px',
        },
    },
    list: {
        class: 'date-range-picker__year-select-list',
        style: {
            padding: '4px',
        },
    },
    option: {
        class: 'date-range-picker__year-select-option',
        style: {
            padding: '6px 4px',
            borderRadius: '8px',
            fontFamily: 'Archivo, sans-serif',
            fontSize: '15px',
            fontWeight: '500',
            lineHeight: '1',
            color: '#344054',
        },
    },
    optionLabel: {
        style: {
            fontFamily: 'Archivo, sans-serif',
            fontSize: '15px',
            fontWeight: '500',
            lineHeight: '1',
        },
    },
};

const displayValue = computed(() => {
    if (!props.modelValue) {
        return 'Select range';
    }

    return props.modelValue
        .map((date) => dateFormatter.format(date))
        .join(' - ');
});

const normalizedDraftRange = computed(() => toCommittedRange(draftRange.value));

// PrimeVue re-derives the visible month on every model change without emitting an event,
// so the toolbar reads its state directly instead of tracking a copy that silently desyncs.
const calendarBaseDate = computed(() => {
    const pickerInstance = datePickerRef.value;

    if (pickerInstance?.currentMonth != null && pickerInstance.currentYear != null) {
        return new Date(pickerInstance.currentYear, pickerInstance.currentMonth, 1);
    }

    const fallbackDate = normalizedDraftRange.value?.[0]
        ?? calendarToJSDate(todayIn(tzStore.effectiveTimezone));

    return new Date(fallbackDate.getFullYear(), fallbackDate.getMonth(), 1);
});

const visibleCalendars = computed<CalendarHeaderItem[]>(() => (
    Array.from({ length: VISIBLE_MONTH_COUNT }, (_, index) => {
        const visibleDate = new Date(
            calendarBaseDate.value.getFullYear(),
            calendarBaseDate.value.getMonth() + index,
            1,
        );

        return {
            key: `${visibleDate.getFullYear()}-${visibleDate.getMonth()}`,
            index,
            month: visibleDate.getMonth(),
            year: visibleDate.getFullYear(),
            monthLabel: monthFormatter.format(visibleDate),
        };
    })
));

const yearOptions = computed(() => {
    const visibleYears = visibleCalendars.value.map((calendar) => calendar.year);
    const currentYear = new Date().getFullYear();
    const startYear = Math.min(...visibleYears, currentYear) - YEAR_OPTIONS_BUFFER;
    const endYear = Math.max(...visibleYears, currentYear) + YEAR_OPTIONS_BUFFER;

    return Array.from(
        { length: endYear - startYear + 1 },
        (_, index) => startYear + index,
    );
});

const activePresetId = computed(() => (
    getMatchingPreset(normalizedDraftRange.value, tzStore.effectiveTimezone)
));

const isApplyDisabled = computed(() => !draftRange.value || draftRange.value.length === 0);

const syncDraftWithModel = () => {
    draftRange.value = toDraftRange(cloneDateRangeValue(props.modelValue));
};

const applyCalendarBaseDate = (baseDate: Date) => {
    const normalizedBaseDate = new Date(
        baseDate.getFullYear(),
        baseDate.getMonth(),
        1,
    );

    if (!datePickerRef.value) {
        return;
    }

    datePickerRef.value.currentView = 'date';
    datePickerRef.value.onYearDropdownChange?.(String(normalizedBaseDate.getFullYear()));
    datePickerRef.value.onMonthDropdownChange?.(String(normalizedBaseDate.getMonth()));
};

const shiftCalendarView = (monthDelta: number) => {
    applyCalendarBaseDate(new Date(
        calendarBaseDate.value.getFullYear(),
        calendarBaseDate.value.getMonth() + monthDelta,
        1,
    ));
};

const updateCalendarYear = (calendar: CalendarHeaderItem, value: number | string) => {
    const targetYear = Number(value);

    if (Number.isNaN(targetYear)) {
        return;
    }

    const selectedMonthDate = new Date(targetYear, calendar.month, 1);
    const nextBaseDate = new Date(
        selectedMonthDate.getFullYear(),
        selectedMonthDate.getMonth() - calendar.index,
        1,
    );

    const [startDate, endDate] = draftRange.value ?? [];

    if (startDate instanceof Date && endDate instanceof Date) {
        const yearDelta = targetYear - calendar.year;
        draftRange.value = [startDate, endDate].map((date) => (
            new Date(
                date.getFullYear() + yearDelta,
                date.getMonth(),
                date.getDate(),
            )
        ));
    }

    nextTick(() => applyCalendarBaseDate(nextBaseDate));
};

const togglePanel = (event: MouseEvent) => {
    syncDraftWithModel();
    popoverRef.value?.toggle(event);
};

// Selecting a date makes PrimeVue re-centre the view on the new range start; the clicked day
// is already visible, so keep the months the user is looking at instead.
const keepCalendarView = () => {
    const pinnedBaseDate = calendarBaseDate.value;

    nextTick(() => applyCalendarBaseDate(pinnedBaseDate));
};

const selectPreset = (presetId: DateRangePresetId) => {
    const range = getPresetRange(presetId, tzStore.effectiveTimezone);
    draftRange.value = [...range];
    nextTick(() => applyCalendarBaseDate(range[0]));
};

const clearDraftRange = () => {
    draftRange.value = null;
    emit('update:modelValue', null);
    popoverRef.value?.hide();
};

const applyDraftRange = () => {
    if (isApplyDisabled.value) {
        return;
    }

    emit('update:modelValue', normalizedDraftRange.value as DateRangeValue);
    popoverRef.value?.hide();
};
</script>

<template>
    <div class="date-range-picker">
        <button
            class="date-range-picker__trigger"
            :style="{
                width: props.triggerWidth,
                minWidth: props.triggerWidth,
                borderRadius: props.triggerRadius,
            }"
            type="button"
            aria-label="Filter by date range"
            aria-haspopup="dialog"
            @click="togglePanel"
        >
            <span class="date-range-picker__trigger-label">{{ props.label }}</span>
            <span class="date-range-picker__trigger-value">{{ displayValue }}</span>
            <svg class="date-range-picker__trigger-chevron" width="12" height="7" viewBox="0 0 12 7" fill="none" aria-hidden="true">
                <path d="M1 1L6 6L11 1" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
        </button>

        <Popover
            ref="popoverRef"
            :pt="{
                root: { class: 'date-range-picker-popover' },
                content: { class: 'date-range-picker-popover__content' },
            }"
        >
            <div class="date-range-picker__panel">
                <div
                    class="date-range-picker__presets"
                    role="listbox"
                    aria-label="Date range presets"
                >
                    <button
                        v-for="preset in dateRangePresets"
                        :key="preset.id"
                        class="date-range-picker__preset"
                        :class="{
                            'date-range-picker__preset--active':
                                activePresetId === preset.id,
                        }"
                        type="button"
                        role="option"
                        :aria-selected="activePresetId === preset.id"
                        @click="selectPreset(preset.id)"
                    >
                        {{ preset.label }}
                    </button>
                </div>

                <div class="date-range-picker__calendar-shell">
                    <div class="date-range-picker__calendar-toolbar">
                        <div
                            v-for="calendar in visibleCalendars"
                            :key="calendar.key"
                            class="date-range-picker__calendar-toolbar-group"
                        >
                            <div class="date-range-picker__calendar-toolbar-title">
                                <span class="date-range-picker__calendar-month-label">
                                    {{ calendar.monthLabel }}
                                </span>

                                <div class="date-range-picker__calendar-year-select-wrap">
                                    <Select
                                        :modelValue="calendar.year"
                                        :options="yearOptions"
                                        size="small"
                                        appendTo="body"
                                        dropdownIcon="pi pi-chevron-down"
                                        :highlightOnSelect="true"
                                        :checkmark="false"
                                        :pt="yearSelectPt"
                                        aria-label="Select year"
                                        @update:modelValue="updateCalendarYear(calendar, $event)"
                                    />
                                </div>
                            </div>

                            <div class="date-range-picker__calendar-toolbar-actions">
                                <button
                                    class="date-range-picker__calendar-nav-button"
                                    type="button"
                                    aria-label="Previous month"
                                    @click="shiftCalendarView(-1)"
                                >
                                    <span
                                        class="pi pi-chevron-left
                                            date-range-picker__calendar-nav-icon"
                                        aria-hidden="true"
                                    />
                                </button>

                                <button
                                    class="date-range-picker__calendar-nav-button"
                                    type="button"
                                    aria-label="Next month"
                                    @click="shiftCalendarView(1)"
                                >
                                    <span
                                        class="pi pi-chevron-right
                                            date-range-picker__calendar-nav-icon"
                                        aria-hidden="true"
                                    />
                                </button>
                            </div>
                        </div>
                    </div>

                    <DatePicker
                        ref="datePickerRef"
                        v-model="draftRange"
                        selectionMode="range"
                        inline
                        :numberOfMonths="2"
                        :manualInput="false"
                        :dt="calendarDt"
                        :pt="calendarPt"
                        @date-select="keepCalendarView"
                    />

                    <div class="date-range-picker__footer">
                        <button
                            class="date-range-picker__footer-action
                                date-range-picker__footer-action--secondary"
                            type="button"
                            @click="clearDraftRange"
                        >
                            Clear
                        </button>

                        <button
                            class="date-range-picker__footer-action
                                date-range-picker__footer-action--primary"
                            type="button"
                            :disabled="isApplyDisabled"
                            @click="applyDraftRange"
                        >
                            Done
                        </button>
                    </div>
                </div>
            </div>
        </Popover>
    </div>
</template>

<style lang="scss" scoped>
.date-range-picker {
    &__trigger {
        display: inline-flex;
        align-items: center;
        justify-content: flex-start;
        gap: 6px;
        width: 265px;
        min-width: 265px;
        height: 46px;
        padding: 0 20px;
        border: 1px solid $border-gray-color;
        border-radius: 20px;
        background: $white-color;
        font-family: $font-family-archivo;
        font-size: 16px;
        font-weight: 400;
        line-height: 17px;
        color: $black-color;
        text-align: left;
        cursor: pointer;
    }

    &__trigger-label {
        color: rgba($black-color, 0.7);
    }

    &__trigger-value {
        color: $black-color;
    }

    &__trigger-chevron {
        color: rgba(0, 0, 0, .6) !important;
        flex-shrink: 0;
        margin-left: auto;
    }

    &__panel {
        display: grid;
        grid-template-columns: 138px minmax(0, 1fr);
        width: 710px;
        height: 346px;
        background: $white-color;
    }

    &__presets {
        display: flex;
        flex-direction: column;
        gap: 2px;
        padding: 16px 12px;
        border-right: 1px solid rgba($black-color, 0.08);
    }

    &__preset {
        display: flex;
        align-items: center;
        width: 100%;
        min-height: 28px;
        padding: 0 10px;
        border: none;
        border-radius: 8px;
        background: transparent;
        font-family: $font-family-archivo;
        font-size: 15px;
        font-weight: 400;
        line-height: 1.2;
        color: rgba($black-color, 0.78);
        text-align: left;
        cursor: pointer;
        transition: background-color 0.15s ease, color 0.15s ease;

        &:hover {
            background: rgba($main-orange, 0.08);
            color: $color-text-primary;
        }

        &--active {
            background: rgba($main-orange, 0.12);
            color: $main-orange;
            font-weight: 500;
        }
    }

    &__calendar-shell {
        display: grid;
        grid-template-rows: auto minmax(0, 1fr) auto;
        min-width: 0;
        min-height: 0;
        padding: 14px 14px 12px;
        overflow: hidden;
    }

    &__calendar-toolbar {
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: 14px;
        padding-bottom: 6px;
    }

    &__calendar-toolbar-group {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 10px;
        min-width: 0;
    }

    &__calendar-toolbar-title {
        display: inline-flex;
        align-items: center;
        gap: 2px;
        min-width: 0;
    }

    &__calendar-toolbar-actions {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        flex-shrink: 0;
    }

    &__calendar-month-label,
    &__year-select-label {
        font-family: $font-family-archivo;
        font-size: 15px;
        font-weight: 600;
        line-height: 1;
        color: $color-text-primary;
    }

    &__calendar-year-select-wrap {
        display: inline-flex;
        align-items: center;
    }

    &__year-select {
        width: auto;
        min-width: 0;
        border: none;
        background: transparent;
        box-shadow: none;
    }

    &__year-select-label {
        padding: 0;
    }

    &__year-select-dropdown {
        width: 10px;
        padding: 0;
        border: none;
        background: transparent;
        color: rgba($black-color, 0.55);
    }

    &__year-select-dropdown-icon {
        font-size: 6px;
    }

    &__year-select-list {
        padding: 4px;
    }

    &__year-select-option {
        border-radius: 8px;
        font-family: $font-family-archivo;
        font-size: 15px;
    }

    :deep(.date-range-picker__year-select.p-select) {
        display: inline-flex;
        align-items: center;
        width: auto;
        min-height: auto;
        min-width: 0;
        gap: 0;
        padding: 0;
    }

    :deep(.date-range-picker__year-select.p-focus) {
        box-shadow: none;
    }

    :deep(.date-range-picker__year-select .p-select-label) {
        padding: 0;
        min-height: auto;
    }

    :deep(.date-range-picker__year-select .p-select-dropdown) {
        margin-left: 0;
        padding: 0;
    }

    &__footer {
        display: flex;
        align-items: center;
        justify-content: flex-end;
        gap: 8px;
    }

    &__footer-action {
        min-width: 76px;
        height: 34px;
        padding: 0 14px;
        border-radius: 999px;
        font-family: $font-family-archivo;
        font-size: 14px;
        font-weight: 500;
        cursor: pointer;

        &:disabled {
            cursor: not-allowed;
            opacity: 0.45;
        }

        &--secondary {
            border: 1px solid $border-gray-color;
            background: $white-color;
            color: rgba($black-color, 0.72);
        }

        &--primary {
            border: 1px solid transparent;
            background: $main-orange;
            color: $white-color;
        }
    }

    &__calendar-nav-button {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 24px;
        height: 24px;
        border: none;
        border-radius: 999px;
        background: transparent;
        color: rgba($black-color, 0.55);
        cursor: pointer;
        transition: background-color 0.15s ease, color 0.15s ease;

        &:hover {
            background: #F2F4F7;
            color: $color-text-primary;
        }
    }

    &__calendar-nav-icon {
        font-size: 6px;
    }

    :deep(.date-range-picker__calendar) {
        width: 100%;
        height: 100%;
        min-width: 0;
        border: none;
        box-shadow: none;
    }

    :deep(.date-range-picker__calendar-container) {
        width: 100%;
        min-width: 0;
    }

    :deep(.p-datepicker) {
        width: 100%;
        height: 100%;
        min-width: 0;
        border: none;
        font-family: $font-family-archivo;
    }

    :deep(.p-datepicker-panel-inline) {
        display: flex;
        flex-direction: column;
        height: 100%;
        border: 0 none;
        background: transparent;
        box-shadow: none;
        overflow-x: hidden;
    }

    :deep(.p-datepicker-calendar-container) {
        width: 100%;
        height: 100%;
        min-width: 0;
        gap: 0;
    }

    :deep(.p-datepicker-calendar-container .p-datepicker-calendar) {
        border-inline-start: 0 none;
        display: flex;
        flex-direction: column;
        flex: 1 1 0;
        height: 100%;
        min-width: 0;
        padding-inline: 0;
    }

    :deep(.p-datepicker-calendar) {
        margin: 0;
    }

    :deep(.p-datepicker-header) {
        display: none;
    }

    :deep(.p-datepicker-prev-button),
    :deep(.p-datepicker-next-button) {
        width: 24px;
        height: 24px;
        color: rgba($black-color, 0.55);
    }

    :deep(.p-datepicker-prev-button .p-icon),
    :deep(.p-datepicker-next-button .p-icon) {
        font-size: 11px;
    }

    :deep(.p-datepicker-day-view) {
        width: 100%;
        height: 100%;
        table-layout: fixed;
    }

    :deep(.p-datepicker-weekday-cell),
    :deep(.p-datepicker-day-cell) {
        padding: 0;
    }

    :deep(.p-datepicker-weekday) {
        text-transform: uppercase;
    }

    :deep(.p-datepicker-day) {
        font-weight: 400;
    }

    :deep(.p-datepicker-other-month .p-datepicker-day) {
        color: rgba($black-color, 0.34);
    }
}

:global(.p-popover.date-range-picker-popover) {
    --p-popover-background: #{$white-color};
    --p-popover-border-color: #{$border-gray-color};
    --p-popover-border-radius: 20px;
    --p-popover-shadow: 8px 18px 60px rgba(0, 0, 0, 0.08);
    --p-popover-content-padding: 0;
    --p-popover-gutter: 14px;
    width: 710px;
    height: 346px;
    background: var(--p-popover-background);
    border: 1px solid $border-gray-color;
    border-radius: var(--p-popover-border-radius);
    box-shadow: var(--p-popover-shadow);
    overflow: hidden;
}

:global(.p-popover.date-range-picker-popover::before),
:global(.p-popover.date-range-picker-popover::after) {
    display: none !important;
}

:global(.date-range-picker-popover__content),
:global(.p-popover.date-range-picker-popover .p-popover-content) {
    padding: 0;
    background: transparent;
}

:global(.date-range-picker__year-select-overlay) {
    border-radius: 12px;
    overflow: hidden;
}

// The popover teleports to body, so calendar rules must be global, not scoped under .date-range-picker.
// PrimeVue skips hover on selected days and lightens hovered in-range days; one darker hover for both.
:global(.date-range-picker-popover .p-datepicker-day-selected:not(.p-disabled):hover),
:global(.date-range-picker-popover .p-datepicker-day-selected-range:not(.p-disabled):hover) {
    background: #c3cad4;
    color: #101828;
}
</style>
